/**
 * COLUMN WIDTH HOOK
 * Manages dynamic column width calculations
 * @version 1.0.0
 * @production-ready
 */

import { useMemo, useRef } from 'react';
import { ColumnDefinition, TableData, TableStyleConfig } from '../types';
import {
  calculateAllColumnWidths,
  calculateStickyLeft,
  calculateTotalTableWidth
} from '../utils';

export interface UseColumnWidthReturn {
  columnWidths: number[];
  totalWidth: number;
  getColumnWidth: (index: number) => number;
  getStickyLeft: (index: number) => number;
  columnsWithSticky: ColumnDefinition[];
}

export const useColumnWidth = (
  columns: ColumnDefinition[],
  data: TableData,
  styleConfig: TableStyleConfig,
  rowKeys?: string[]
): UseColumnWidthReturn => {
  /**
   * Calculate all column widths.
   * Content sampling reads the latest `data` via a ref, but the memo itself keys off
   * `rowKeys` (stable across single-cell edits — `updateCell` only replaces `data`,
   * never `rowKeys`) instead of `data`'s reference. This means width recalculates when
   * rows are added/removed/loaded, but NOT on every keystroke — avoiding an
   * O(sampleSize x columns) text-width recompute per edit (previously the #1 hidden
   * cause of per-keystroke lag across all 4 sheets using this shared table).
   */
  const dataRef = useRef(data);
  dataRef.current = data;
  const rowKeysDep = rowKeys ?? Object.keys(data);
  const columnWidths = useMemo(() => {
    return calculateAllColumnWidths(columns, dataRef.current, styleConfig);
  }, [columns, styleConfig, rowKeysDep]);

  /**
   * Calculate total table width
   */
  const totalWidth = useMemo(() => {
    return calculateTotalTableWidth(columnWidths);
  }, [columnWidths]);

  /**
   * Enhance columns with sticky positions
   */
  const columnsWithSticky = useMemo(() => {
    return columns.map((col, index) => ({
      ...col,
      stickyLeft: col.sticky ? calculateStickyLeft(columns, columnWidths, index) : 0
    }));
  }, [columns, columnWidths]);

  /**
   * Get width for specific column
   */
  const getColumnWidth = (index: number): number => {
    return columnWidths[index] || 120;
  };

  /**
   * Get sticky left position for column
   */
  const getStickyLeft = (index: number): number => {
    return columnsWithSticky[index]?.stickyLeft || 0;
  };

  return {
    columnWidths,
    totalWidth,
    getColumnWidth,
    getStickyLeft,
    columnsWithSticky
  };
};
